import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Search, SlidersHorizontal } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  Pagination,
  type ColumnDef,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { UseQueryResult } from '@tanstack/react-query';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import type { ResponseArray } from '@/types/shared';
import {
  type OpsResourceParams,
  type OpsResourceSummary,
} from '@/api/operations-resources';
import { OpsResourceStatBand } from './ops-resource-stat-band';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value to avoid spamming the backend. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Fold FilterBuilder conditions into backend params (provider/status/org_id). */
function conditionsToParams(conditions: FilterCondition[]): Partial<OpsResourceParams> {
  const out: Partial<OpsResourceParams> = {};
  for (const c of conditions) {
    if (!c.value) continue;
    if (c.field === 'provider') out.provider = c.value;
    else if (c.field === 'status') out.status = c.value;
    else if (c.field === 'org_id') out.org_id = c.value;
  }
  return out;
}

interface Props<T> {
  title: string;
  description: string;
  searchPlaceholder: string;
  kindLabel: string;
  emptyIcon: ReactNode;
  emptyTitle: string;
  columns: ColumnDef<T, unknown>[];
  toggleableColumnIds: string[];
  filterFields: FilterField[];
  useList: (params: OpsResourceParams) => UseQueryResult<ResponseArray<T>>;
  useSummary: (params: OpsResourceParams) => UseQueryResult<OpsResourceSummary>;
}

/**
 * Shared cross-org resource-inventory page (Instances / Domains). Layout:
 * PageHeader → dashboard-style stat band (reflects current filter) → Toolbar
 * (search + Filters + Display options) → server-paginated DataTable.
 */
export function OperationsResourceInventoryPage<T extends { id: string; organization_id: string }>({
  title,
  description,
  searchPlaceholder,
  kindLabel,
  emptyIcon,
  emptyTitle,
  columns,
  toggleableColumnIds,
  filterFields,
  useList,
  useSummary,
}: Props<T>) {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(toggleableColumnIds.map((id) => [id, true])),
  );

  const filterParams = conditionsToParams(conditions);
  // Summary shares the filter (minus pagination) so the stat band matches the list.
  const summaryParams: OpsResourceParams = { q: query || undefined, ...filterParams };

  const { data, isLoading, isError, isFetching, refetch } = useList({
    page,
    per_page: PAGE_SIZE,
    ...summaryParams,
  });
  const summaryQuery = useSummary(summaryParams);

  const rows: T[] = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = columns.filter(
    (c) => !toggleableColumnIds.includes(String(c.id)) || visible[String(c.id)] !== false,
  );
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };
  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PageHeader title={title} description={description} />

        <OpsResourceStatBand
          summary={summaryQuery.data}
          isLoading={summaryQuery.isLoading}
          kindLabel={kindLabel}
        />

        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={rawQuery}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              startSlot={<Search size={15} />}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            <>
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters{' '}
                    {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" style={{ padding: 0 }}>
                  <FilterBuilder
                    fields={filterFields}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={applyFilters}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                    Display options
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {columns
                    .filter((c) => toggleableColumnIds.includes(String(c.id)))
                    .map((c) => {
                      const id = String(c.id);
                      return (
                        <DropdownMenuCheckboxItem
                          key={id}
                          checked={visible[id] !== false}
                          onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [id]: v === true }))}
                          onSelect={(e) => e.preventDefault()}
                        >
                          {typeof c.header === 'string' ? c.header : id}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />

        {isError ? (
          <Alert
            tone="danger"
            title={`Failed to load ${kindLabel}`}
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <TabLoading rows={8} />
        ) : (
          <DataTable
            columns={visibleColumns}
            data={rows}
            getRowId={(r) => r.id}
            onRowClick={(row) => navigate(`/operations/orgs/${row.organization_id}`)}
            emptyState={
              <EmptyState
                icon={emptyIcon}
                title={emptyTitle}
                description="Try adjusting your search or filters."
              />
            }
            footer={(() => {
              const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
              return (
                <Pagination
                  page={page}
                  pageCount={view.pageCount}
                  onPageChange={setPage}
                  summary={`${view.summary}${isFetching ? ' …' : ''}`}
                />
              );
            })()}
          />
        )}
      </div>
    </Container>
  );
}
